(ns hypnos.backoffice.tile.field.patient-image
  "Patient image/portrait."
  (:require [goog.fs.url :as url]
            [hypnos.i18n :refer [tr]]
            [re-frame.core :as rf]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;; Events

(rf/reg-event-db
 :portrait/set-file
 (fn [db [_ file]]
   (-> db
       (assoc-in [:portrait :file] file)
       (assoc-in [:portrait :upload-status] nil))))

(rf/reg-event-db
 :portrait/uploading
 (fn [db _]
   (assoc-in db [:portrait :upload-status] :uploading)))

(rf/reg-event-db
 :portrait/uploaded
 (fn [db [_ {:keys [data _errors]}]]
   (-> db
       (assoc-in [:portrait :upload-status] (when data :uploaded))
       (assoc-in [:portrait :issues] (or (:issues data) [])))))

(rf/reg-event-db
 :portrait/failed
 (fn [db _]
   (assoc-in db [:portrait :upload-status] :failed)))

(rf/reg-sub
 :portrait/upload-status
 (fn [db _]
   (get-in db [:portrait :upload-status])))

(rf/reg-sub
 :portrait/current
 (fn [db _]
   (get-in db [:portrait])))

(rf/reg-sub
 :portrait/issues
 (fn [db _]
   (get-in db [:portrait :issues])))


;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- determine-portrait-status
  "Determine the current state of the portrait-taking process."
  [portrait upload-status issues]
  (let [file?      (:file portrait)
        new?       (nil? upload-status)
        uploading? (= :uploading upload-status)
        uploaded?  (= :uploaded upload-status)
        issues?    (seq issues)]
    (cond
      (and (not file?) new? (not issues?)) :new
      (and file? new? (not issues?))       :picture
      uploading?                           :uploading
      (and file? uploaded? (not issues?))  :no-issue
      (and file?
           (or uploaded? new?)
           issues?)                        :issue
      ;; (and file? new? issues?)             :retake
      :else                                :existing)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Field

(defn issues
  "Any issued with the image."
  []
  (let [issues (rf/subscribe [:portrait/issues])]
    (when (seq @issues)
      [:div
       [:span.has-text-weight-semibold "Issue(s): "]
       [:div.tags
        (for [issue @issues]
          ^{:key (str "portrait-issue-" issue)}
          [:span.tag.is-danger
           [:span.has-tooltip-arrow.has-tooltip-multiline.has-tooltip-right
            {:data-tooltip (tr (keyword (str "ui.portrait-issue-long/" issue)))}
            (tr (keyword (str "ui.portrait-issue-short/" issue)))]])]])))

(defn image
  "The image itself."
  []
  (let [patient  (rf/subscribe [:backoffice/patient])
        portrait (rf/subscribe [:portrait/current])]
    [:figure.image.is-3by4
     (if (:file @portrait)
       [:a {:href (url/createObjectUrl (:file @portrait)) :target "_blank"}
        [:img {:src (url/createObjectUrl (:file @portrait))}]]
       [:img {:src (if (= :gender/female (:patient/gender @patient))
                     "/images/female-profile-front.png"
                     "/images/male-profile-front.png")}])]))

(defn image-field
  "The image field."
  []
  (let [patient       (rf/subscribe [:backoffice/patient])
        last-consult  (:patient/last-consult @patient)
        portrait      (rf/subscribe [:portrait/current])
        upload-status (rf/subscribe [:portrait/upload-status])
        issues        (rf/subscribe [:portrait/issues])
        status        (determine-portrait-status @portrait @upload-status @issues)]
    (case status
      ;; no picture taken
      :new
      [:label.upload.control
       [:a.button.is-success.is-small.is-light
        [:span.icon [:i.mdi.mdi-camera]]
        [:span (tr :ui.label/take!)]]
       [:input.file-input
        {:type      :file
         :capture   :camera
         :accept    "image/*"
         :id        "portait-front"
         :name      "portait-front"
         :on-change (fn [_]
                      (let [el    (.getElementById js/document "portait-front")
                            file  (aget (.-files el) 0)
                            _name (.-name file)]
                        (rf/dispatch [:portrait/set-file file])))}]]
      ;; picture was taken but not uploaded yet
      :picture
      [:div.field.is-grouped
       [:p.control
        [:button.button.is-success.is-small.is-light
         {:disabled (contains? #{:uploading :uploaded} @upload-status)
          :on-click (fn [_]
                      (rf/dispatch-sync [:portrait/uploading])
                      (rf/dispatch [:amplify/upload-file-by-therapist
                                    (:consult/key last-consult)
                                    :portrait/uploaded]))}
         [:span.icon [:i.mdi.mdi-cloud-upload]]
         [:span (tr :ui.label/upload)]]]
       [:div.field.file
        [:label.upload.control
         [:a.button.is-success.is-small.is-light
          [:span.icon [:i.mdi.mdi-camera]]
          [:span (tr :ui.label/re-take)]]
         [:input.file-input
          {:type      :file
           :capture   :camera
           :accept    "image/*"
           :id        "portait-front"
           :name      "portait-front"
           :on-change (fn [_]
                        (let [el    (.getElementById js/document "portait-front")
                              file  (aget (.-files el) 0)
                              _name (.-name file)]
                          (rf/dispatch-sync [:portrait/set-file file])))}]]]]
      ;; currently uploading
      :uploading
      [:div.field
       [:p.control
        [:span.button.is-small.is-info.is-outlined
         [:span.icon [:i.mdi.mdi-sync.mdi-spin]]
         [:span (tr :ui.label/uploading)]]]]
      ;; portrait was uploaded and no issues
      :no-issue
      nil
      ;; re-take
      :retake
      [:div.field.is-grouped
       [:p.control
        [:button.button.is-success.is-small.is-light
         {:disabled (contains? #{:uploading :uploaded} @upload-status)
          :on-click (fn [_]
                      (rf/dispatch-sync [:portrait/uploading])
                      (rf/dispatch [:amplify/upload-file-by-therapist
                                    (:consult/key last-consult)
                                    :portrait/uploaded]))}
         [:span.icon [:i.mdi.mdi-cloud-upload]]
         [:span (tr :ui.label/upload)]]]
       [:div.field.file
        [:label.upload.control
         [:a.button.is-success.is-small.is-light
          [:span.icon [:i.mdi.mdi-camera]]
          [:span (tr :ui.label/re-take)]]
         [:input.file-input
          {:type      :file
           :capture   :camera
           :accept    "image/*"
           :id        "portait-front"
           :name      "portait-front"
           :on-change (fn [_]
                        (let [el    (.getElementById js/document "portait-front")
                              file  (aget (.-files el) 0)
                              _name (.-name file)]
                          (rf/dispatch-sync [:portrait/set-file file])))}]]]]
      ;; portrait was uploaded and issues found
      :issue
      [:div.field.is-grouped
       [:p.control
        [:button.button.is-success.is-small.is-light
         {:disabled (contains? #{:uploading :uploaded} @upload-status)
          :on-click (fn [_]
                      (rf/dispatch-sync [:portrait/uploading])
                      (rf/dispatch [:amplify/upload-file-by-therapist
                                    (:consult/key last-consult)
                                    :portrait/uploaded]))}
         [:span.icon [:i.mdi.mdi-cloud-upload]]
         [:span (tr :ui.label/upload)]]]
       [:div.field.file
        [:label.upload.control
         [:a.button.is-success.is-small.is-light
          [:span.icon [:i.mdi.mdi-camera]]
          [:span (tr :ui.label/re-take)]]
         [:input.file-input
          {:type      :file
           :capture   :camera
           :accept    "image/*"
           :id        "portait-front"
           :name      "portait-front"
           :on-change (fn [_]
                        (let [el    (.getElementById js/document "portait-front")
                              file  (aget (.-files el) 0)
                              _name (.-name file)]
                          (rf/dispatch-sync [:portrait/set-file file])))}]]]])))
